// ------------------------------------
// Constants
// ------------------------------------
import axios from 'axios'
import constants from '../lib/constants'
import math from '../lib/math'
import _ from 'lodash'
export const LOAD_JOBS = 'LOAD_JOBS'
export const LOAD_IMAGES = 'LOAD_IMAGES'
export const MARK_IMAGE = 'MARK_IMAGE'
export const CREATE_PLACEHOLDER_JOB = 'CREATE_PLACEHOLDER_JOB'

// ------------------------------------
// Helpers
// ------------------------------------

const augmentJobData = (job) => {
  job.cellsPerImage = []
  let neutrophilsPerImage = []
  let lymphocytesPerImage = []
  job.xPositions = []
  let totalValidImages = 0
  job.totalDifferentiableCells = 0

  job.lymphocytesPerImageCenter = []
  job.neutrophilsPerImageCenter = []

  _.forEach(job.images, (imageObj) => {
    const imageName = _.last(imageObj.raw.split('/'))
    job.xPositions.push(imageName.split('_')[0])
    if (imageObj['is_ignored'] && !_.includes(job.strip_type, [constants.STRIP_TYPES.WBC_CONTROL, constants.STRIP_TYPES.WBC_CONTROL_ATH_CHECK])) {
      job.cellsPerImage.push(0)
      job.lymphocytesPerImageCenter.push(0)
      lymphocytesPerImage.push(0)
      job.neutrophilsPerImageCenter.push(0)
      neutrophilsPerImage.push(0)
      return
    } else {
      totalValidImages += 1
      let numNeutrophilsInImage = 0
      let numLymphocytesInImage = 0
      let neutrophilsInCenter = 0
      let lymphocytesInCenter = 0
      job.cellsPerImage.push((imageObj.keypoints && imageObj.keypoints.true && imageObj.keypoints.true.length) || 0)
      if (imageObj.keypoints) {
        _.forEach(imageObj.keypoints.true, (keypoint) => {
          if (keypoint.note.indexOf('wbc') > -1 && keypoint.x > 0.2 * 3200 && keypoint.x < 0.8 * 3200 && keypoint.y > 0.2 * 2400 && keypoint.y < 0.8 * 2400) {
            if (keypoint.note.indexOf('wbc_p') > -1) {
              neutrophilsInCenter += 1
            } else if (keypoint.note.indexOf('wbc_m') > -1) {
              lymphocytesInCenter += 1
            }
          }
          if (keypoint.note.indexOf('wbc_') > -1 && keypoint.note.indexOf('wbc_u') === -1) {
            job.totalDifferentiableCells += 1
          }
          if (keypoint.note === 'wbc_p' || keypoint.note === 'wbc_p_n') {
            numNeutrophilsInImage += 1
          } else if (keypoint.note === 'wbc_m' || keypoint.note === 'wbc_m_l') {
            numLymphocytesInImage += 1
          }
        })
      }
      job.lymphocytesPerImageCenter.push(lymphocytesInCenter)
      lymphocytesPerImage.push(numLymphocytesInImage)
      job.neutrophilsPerImageCenter.push(neutrophilsInCenter)
      neutrophilsPerImage.push(numNeutrophilsInImage)
    }
  })

  job.neutrophilsPerImageOuter = _.map(neutrophilsPerImage, (cells, i) => {
    if (cells > 0) {
      return cells - job.neutrophilsPerImageCenter[i]
    } else {
      return 0
    }
  })

  job.lymphocytesPerImageOuter = _.map(lymphocytesPerImage, (cells, i) => {
    if (cells > 0) {
      return cells - job.lymphocytesPerImageCenter[i]
    } else {
      return 0
    }
  })

  job.totalNeutrophils = _.sum(neutrophilsPerImage)
  job.totalNumCells = _.sum(job.cellsPerImage)
  job.averageNumCells = job.totalNumCells / totalValidImages

  let calibrationFactor = constants.CALIBRATION_FACTORS['4x']
  if (job.strip_type === constants.STRIP_TYPES.WBC_CONTROL || job.strip_type === constants.STRIP_TYPES.WBC_CONTROL_HIGH) {
    calibrationFactor = constants.CALIBRATION_FACTORS['CONTROL'];
  }

  job.predictedWBCCount = job.averageNumCells * calibrationFactor

  job.predictedWbcCountsByImages = _.map(_.range(0, _.size(job.images)), (lastImageNumber) => {
    return calibrationFactor * _.sum(job.cellsPerImage.slice(0, lastImageNumber + 1)) / (lastImageNumber + 1)
  })

  job.neutrophilPercentPerImage = _.map(job.cellsPerImage, (cells, index) => {
    if (cells > 0) {
      return neutrophilsPerImage[index] / cells * 100
    } else {
      return 0
    }
  })

  job.neutrophilPercentCenterPerImage = _.map(job.neutrophilsPerImageCenter, (cells, index) => {
    if (cells > 0) {
      return job.neutrophilsPerImageCenter[index] / (job.neutrophilsPerImageCenter[index] + job.lymphocytesPerImageCenter[index]) * 100
    } else {
      return 0
    }
  })

  job.neutrophilPercentOuterPerImage = _.map(job.cellsPerImage, (cells, index) => {
    if (cells > 0) {
      const neutrophilsOuter = neutrophilsPerImage[index] - job.neutrophilsPerImageCenter[index]
      const lymphocytesOuter = lymphocytesPerImage[index] - job.lymphocytesPerImageCenter[index]
      return (neutrophilsOuter / (neutrophilsOuter + lymphocytesOuter)) * 100
    } else {
      return 0
    }
  })

  job.wbcCV = math.cv(_.filter(job.cellsPerImage, (numCells) => numCells > 0))
  job.neutrophilStdDev = math.stdDev(_.filter(job.neutrophilPercentPerImage, (percent) => percent > 0))
  job.neutrophilStdDevCenter = math.stdDev(_.filter(job.neutrophilPercentCenterPerImage, (percent) => percent > 0))

  return job
}



// ------------------------------------
// Actions
// ------------------------------------

/*  This is a thunk, meaning it is a function that immediately
    returns a function for lazy evaluation. It is incredibly useful for
    creating async actions, especially when combined with redux-thunk! */

export const loadJobsForDevice = (deviceString, page = 1) => {
  return (dispatch, getState) => {
    return axios.get(`${constants.API_URL}/devices/${deviceString}/jobs`, {
      params: {
        page_size: 10,
        page
      }
    })
    .then((response) => {
      const jobs = response.data.jobs
      dispatch({
        type: LOAD_JOBS,
        payload: jobs
      })
    })
  }
}

export const loadJob = (jobId) => {
  return (dispatch) => {
    return axios.get(`${constants.API_URL}/jobs/${jobId}/`)
    .then((response) => {
      const job = response.data
      dispatch({
        type: LOAD_JOBS,
        payload: [job]
      })
      return job
    })
  }
}

export const cancelJob = (jobId) => {
  return (dispatch) => {
    return axios.post(`${constants.API_URL}/jobs/${jobId}/mark_cancel_requested`)
    .then((response) => {
      const job = response.data
      dispatch({
        type: LOAD_JOBS,
        payload: [job]
      })
      return job
    })
  }
}

export const passJob = (jobId) => {
  return (dispatch) => {
    return axios.post(`${constants.API_URL}/jobs/${jobId}/force_pass`)
    .then((response) => {
      return response.data
    })
  }
}

export const loadImagesForJob = (jobId) => {
  return (dispatch, getState) => {
    return axios.get(`${constants.API_URL}/jobs/${jobId}/`)
    .then((response) => {
      const job = response.data
      return axios.get(`${constants.API_URL}/jobs/${jobId}/get_images`)
      .then((response) => {
        _.forEach(response.data.images, (imageObj) => {
          imageObj.flaggedUrl = imageObj.flagged
          imageObj.markedUrl = imageObj.marked
          imageObj.rawUrl = imageObj.raw
        })
        const jobData = _.extend({ jobId }, job, response.data)
        dispatch({
          type: LOAD_IMAGES,
          payload: jobData
        })
        return jobData
      })
    })
  }
}

export const markImageLabeled = (jobId, imgName, isLabeled) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/jobs/${jobId}/mark_images`, {
      image_values: [{
        name: imgName.replace('ignored_', ''),
        'is_labeled': isLabeled
      }]
    })
    .then((response) => {
      dispatch({
        type: MARK_IMAGE,
        payload: _.extend({ jobId }, response.data)
      })
    })
  }
}

export const markImageIgnored = (jobId, imgName, isIgnored) => {
  return (dispatch, getState) => {
    return axios.post(`${constants.API_URL}/jobs/${jobId}/mark_images`, {
      image_values: [{
        name: imgName.replace('ignored_', ''),
        'is_ignored': isIgnored
      }]
    })
    .then((response) => {
      dispatch({
        type: MARK_IMAGE,
        payload: _.extend({ jobId }, response.data)
      })
    })
  }
}

export const reenqueueJob = (jobId) => {
  return () => {
    return axios.post(
      `${constants.API_URL}/jobs/${jobId}/reenqueue_processing`,
    )
  }
}

export const loadAllJobs = (siteId, patientId = null) => {
  return (dispatch, getState) => {

    return axios.get(`${constants.API_URL}/sites/${siteId}/jobs`, {
      params: {
        page:1,
        page_size: 100000,
        patient_id: patientId
      }
    })
    .then((response) => {
      dispatch({
        type: LOAD_JOBS,
        payload: response.data.jobs
      })

      return response.data.jobs
    })
  }
}

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [CREATE_PLACEHOLDER_JOB]: (state, action) => {
    const jobId = action.payload
    return _.merge({}, state, {
      [jobId]: {
        job_status: constants.JOB_STATUSES.QUEUED,
        jobId: jobId,
        status: 0
      }
    })
  },
  [LOAD_JOBS]    : (state, action) => {
    const jobs = action.payload
    const jobsDictionary = _.zipObject(
      _.map(jobs, (j) => j.job_id),
      jobs
    )
    const newState = _.merge({}, state, jobsDictionary)
    return newState
  },
  [LOAD_IMAGES] : (state, action) => {
    const jobId = action.payload.jobId
    return _.merge({}, state, {
      [jobId]: augmentJobData(action.payload)
    })
  },
  [MARK_IMAGE]: (state, action) => {
    const jobId = action.payload.jobId
    return _.merge({}, state, {
      [jobId]: action.payload
    })
  }
}

// ------------------------------------
// Reducer
// ------------------------------------
const initialState = {}
export default function jobsReducer (state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type]

  return handler ? handler(state, action) : state
}



// WEBPACK FOOTER //
// ./src/store/jobs.js